import { Flex } from '@solinkcorp/solink-components';
import { getLocalTimeZone } from '@solinkcorp/utils';
import classNames from 'classnames';
import FormattedTime from 'components/FormattedTime';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import styles from './EventVerify.module.sass';

interface VerificationConclusionProps {
  /** Conclusion text to display (conclusionReason || conclusion). */
  result?: string;
  /** When true, render the verified-by / updated-on metadata block. */
  showMetadata?: boolean;
  verifiedBy?: string;
  updatedTime?: number | Date;
  /** When true, render the "verified workflow not found" notice. */
  verifiedWorkflowNotFound?: boolean;
  /** Optional interactive affordance shown beside the result pill (e.g. restart/remove). */
  resultAction?: React.ReactNode;
  /** Optional interactive affordance shown to the right of the result (e.g. audit log). */
  trailing?: React.ReactNode;
  /** Render the result pill for a light surface (dark text + neutral outline). */
  plain?: boolean;
}

/**
 * Presentational, read-only display of an event's verification conclusion: the
 * conclusion text, optional verified-by/updated-on metadata, and the
 * workflow-not-found notice. Interactive affordances (restart, audit log) are
 * injected by the caller via the `resultAction`/`trailing` slots, so when those
 * are omitted the component renders with no mutating controls.
 */
const VerificationConclusion: React.FC<VerificationConclusionProps> = ({
  result,
  showMetadata = false,
  verifiedBy,
  updatedTime,
  verifiedWorkflowNotFound = false,
  resultAction,
  trailing,
  plain = false,
}) => {
  const locale = useLocale();
  const resultButtonClass = classNames(styles.button, styles.resultButton, {
    [styles.buttonPlain]: plain,
  });

  return (
    <div className={styles.results}>
      <Flex justifyContent="space-between" alignItems="center">
        {result && (
          <div className={styles.resultHoverWrapper}>
            <div className={resultButtonClass} key={result}>
              {result}
            </div>
            {resultAction}
          </div>
        )}
        {trailing}
      </Flex>
      {showMetadata && (
        <>
          <div>
            {locale.eventState.verifiedBy}: {verifiedBy}
          </div>
          {updatedTime && (
            <div>
              <span>{locale.eventState.updatedOn}: </span>
              <span>
                <FormattedTime
                  date={updatedTime}
                  withDate="date-first"
                  timezoneId={getLocalTimeZone()}
                  dateFormatString={locale.date.format1}
                />
              </span>
            </div>
          )}
        </>
      )}
      {verifiedWorkflowNotFound && <div>{locale.eventFlow.verifiedWorkflowNotFound}</div>}
    </div>
  );
};

export default VerificationConclusion;
